import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchConversationById,
  markConversationAsRead,
  addMessage,
  updateMessageStatus,
  clearCurrentConversation,
  clearError
} from '../store/slices/conversationsSlice';
import { apiService } from '../services/api';
import socketService from '../services/socketService';
import './Chat.css';
import { FiMessageSquare } from 'react-icons/fi';
const Chat = ({ customerId: propCustomerId, customer: propCustomer }) => {
  const { customerId: paramCustomerId } = useParams();
  const customerId = propCustomerId || paramCustomerId;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const mediaBaseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';
  // Redux selectors
  const { currentConversation, currentMessages, currentCustomer, loadingConversation, error, phoneNumberId, customerPhone } = useSelector(
    state => state.conversations
  );
  const { selectedPhoneNumber, phoneNumbers: phoneNumbersList } = useSelector(
    state => state.phoneNumbers
  );

  // Derive effective values — fall back to URL param for phone, and selected phone number for phoneNumberId
  const effectivePhoneNumberId = phoneNumberId || selectedPhoneNumber?.id || phoneNumbersList?.[0]?.id;
  const effectiveCustomerPhone = customerPhone || customerId;
  

  // Local state
  const [message, setMessage] = useState('');
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [isSendingWelcome, setIsSendingWelcome] = useState(false);
  const messagesEndRef = useRef();
  const attachMenuRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const documentInputRef = useRef(null);
  // Get current business account ID (should come from context or selected account)
  // Fallback to prop customer if Redux data not available
  const customer = currentCustomer || propCustomer || {
    id: customerId,
    name: 'Customer',
    phone: 'N/A',
    profilePictureUrl: null
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [currentMessages]);

  // Fetch conversation on component mount
  useEffect(() => {
    if (customerId) {
      // Connect to Socket.IO if not already connected
      if (!socketService.isConnected) {
        socketService.connect();
      }

      // Join conversation room for real-time updates
      socketService.joinConversation(customerId);

      // customerId from URL is actually conversationId from backend
      dispatch(fetchConversationById(customerId))
        .unwrap()
        .catch(err => {
          console.error('Error fetching conversation:', err);
          // Treat fetch failure as a new/empty conversation so the user can still send messages
          dispatch(clearError());
        });

      // Mark conversation as read
      dispatch(markConversationAsRead(customerId))
        .catch(err => {
          console.error('Error marking as read:', err);
        });
    }

    return () => {
      // Leave conversation room when component unmounts
      if (customerId) {
        socketService.leaveConversation(customerId);
      }
      dispatch(clearCurrentConversation());
    };
  }, [customerId, dispatch]);

  // Setup Socket.IO listeners for real-time messages
  useEffect(() => {
    // Listen for new incoming messages
    const handleNewMessage = (data) => {
      console.log('📨 New message from socket:', data);
      if (data.conversationId === customerId) {
        dispatch(addMessage(data.message));
        scrollToBottom();
      }
    };

    // Listen for message status updates
    const handleStatusUpdate = (data) => {
      console.log('📌 Status update from socket:', data);
      if (data.conversationId === customerId) {
        dispatch(updateMessageStatus({
          messageId: data.messageId,
          status: data.status
        }));
      }
    };

    // Register listeners
    socketService.on('new_message', handleNewMessage);
    socketService.on('message_status_update', handleStatusUpdate);

    // Cleanup listeners when component unmounts
    return () => {
      socketService.off('new_message', handleNewMessage);
      socketService.off('message_status_update', handleStatusUpdate);
    };
  }, [customerId, dispatch]);

  // Handle invoice sending from navigation state
  useEffect(() => {
    const state = location.state;
    if (state && state.action === 'send_invoice' && state.invoice && state.pdfData) {
      // Auto-populate message with invoice details
      setMessage(state.message || '');
      
      // Add PDF as attachment
      if (state.pdfData) {
        const pdfFile = {
          id: Date.now(),
          type: 'document',
          name: state.pdfData.filename || `invoice-${state.invoice.invoiceNumber}.pdf`,
          size: 0, // Will be set when file is loaded
          file: state.pdfData.blob,
          preview: null,
          isInvoice: true,
          invoice: state.invoice
        };
        setAttachedFiles([pdfFile]);
      }
      
      // Clear navigation state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Close attach menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (attachMenuRef.current && !attachMenuRef.current.contains(event.target)) {
        // Check if click is not on the attach button
        if (!event.target.closest('.attach-button')) {
          setShowAttachMenu(false);
        }
      }
    };

    if (showAttachMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAttachMenu]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const hasContent = message.trim() || attachedFiles.length > 0;
    
    if (!hasContent || !customerId) {
      console.warn('Missing required data:', { customerId, phoneNumberId, customerPhone });
      return;
    }

    setIsSending(true);
    try {
      // Send text message only (no attachments)
      if (message.trim() && attachedFiles.length === 0) {
        const response = await apiService.messages.sendText({
          phoneNumberId: effectivePhoneNumberId,
          toPhoneNumber: effectiveCustomerPhone,
          messageText: message.trim()
        });

        const newMessage = {
          id: response.data.waMessageId || Date.now().toString(),
          text: message.trim(),
          sender: 'business',
          direction: 'outbound',
          messageType: 'text',
          status: 'sent',
          timestamp: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          conversationId: customerId,
          content: {
            text: message.trim()
          }
        };

        dispatch(addMessage(newMessage));
        setMessage('');
        setAttachedFiles([]);
        setShowAttachMenu(false);
      } 
      // Send media files
      else if (attachedFiles.length > 0) {
        // Send each file separately
        for (const file of attachedFiles) {
          try {
            let response;
            const fileData = {
              phoneNumberId: effectivePhoneNumberId,
              toPhoneNumber: effectiveCustomerPhone,
              file: file.file
            };

            // Determine media type and send accordingly
            if (file.type === 'image') {
              fileData.caption = message.trim() || null;
              response = await apiService.messages.sendImage(fileData);
            } else if (file.type === 'document') {
              fileData.filename = file.name;
              response = await apiService.messages.sendDocument(fileData);
            } else if (file.type === 'video') {
              fileData.caption = message.trim() || null;
              response = await apiService.messages.sendVideo(fileData);
            } else if (file.type === 'audio') {
              response = await apiService.messages.sendAudio(fileData);
            }

            if (response?.data?.message) {
              const newMessage = {
                id: response.data.message._id || response.data.waMessageId || Date.now().toString(),
                sender: 'business',
                direction: 'outbound',
                messageType: file.type,
                status: 'sent',
                timestamp: new Date().toISOString(),
                createdAt: new Date().toISOString(),
                conversationId: customerId,
                content: {
                  text: message.trim() || null,
                  mediaUrl: response.data.message.content?.mediaUrl || null,
                  filename: response.data.message.content?.filename || file.name,
                  mimeType: file.file.type
                },
                attachments: [{
                  id: file.id,
                  type: file.type,
                  name: file.name,
                  preview: file.preview
                }]
              };

              dispatch(addMessage(newMessage));
            }
          } catch (fileError) {
            console.error(`Error sending ${file.type}:`, fileError);
            // Continue with next file even if one fails
          }
        }

        setMessage('');
        setAttachedFiles([]);
        setShowAttachMenu(false);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsSending(false);
    }
  };

  const handleAttachClick = () => {
    setShowAttachMenu(!showAttachMenu);
  };

  const handleFileSelect = (type, event) => {
    const files = Array.from(event.target.files);
    const newFiles = files.map(file => ({
      id: Date.now() + Math.random(),
      file: file,
      type: type,
      name: file.name,
      size: file.size,
      preview: type === 'image' ? URL.createObjectURL(file) : null
    }));

    setAttachedFiles(prev => [...prev, ...newFiles]);
    setShowAttachMenu(false);
    
    // Reset input
    event.target.value = '';
  };

  const handleRemoveAttachment = (fileId) => {
    setAttachedFiles(prev => {
      const updated = prev.filter(f => f.id !== fileId);
      // Revoke object URL if it's an image
      const removed = prev.find(f => f.id === fileId);
      if (removed && removed.preview) {
        URL.revokeObjectURL(removed.preview);
      }
      return updated;
    });
  };

  const handleAttachOption = (option) => {
    setShowAttachMenu(false);
    
    switch(option) {
      case 'document':
        documentInputRef.current?.click();
        break;
      case 'image':
        imageInputRef.current?.click();
        break;
      case 'video':
        videoInputRef.current?.click();
        break;
      case 'location':
        // In a real app, this would open a location picker
        alert('Location picker would open here');
        break;
      case 'contact':
        // In a real app, this would open a contact picker
        alert('Contact picker would open here');
        break;
      case 'catalog':
        // In a real app, this would open the catalog
        navigate('/catalog');
        break;
      default:
        break;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const handleSendWelcome = async () => {
    if (!effectiveCustomerPhone || isSendingWelcome) return;
    setIsSendingWelcome(true);
    try {
      await apiService.messages.sendWelcome({ toPhoneNumber: effectiveCustomerPhone });
      // Refresh conversation to show the welcome message
      dispatch(fetchConversationById(customerId))
        .unwrap()
        .catch(() => {});
    } catch (err) {
      console.error('Error sending welcome message:', err);
      alert('Failed to send welcome message: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsSendingWelcome(false);
    }
  };

  // Transform messages for display
  const displayMessages = currentMessages.map(msg => {
    // Construct full media URL if mediaUrl exists in content
    let fullMediaUrl = null;
    if (msg.content && msg.content.mediaUrl) {
      fullMediaUrl = `${mediaBaseUrl}${msg.content.mediaUrl}`;
    }

    return {
      ...msg,
      fullMediaUrl,
      sender: msg.direction === 'outbound' ? 'business' : 'customer',
      timestamp: msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      }) : new Date().toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      }),
      date: msg.timestamp ? new Date(msg.timestamp).toLocaleDateString() : 'Today'
    };
  });

  const groupedMessages = displayMessages.reduce((acc, msg) => {
    const date = msg.date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(msg);
    return acc;
  }, {});

  return (
    <div className="chat-container">
      <div className="chat-header">
        <button className="back-button" onClick={() => navigate('/customers')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <div className="customer-avatar-small">
          {customer.avatar || '👤'}
        </div>
        <div className="chat-header-info">
          <h2>{customer.name}</h2>
          <p className="customer-status">
            <span className={`status-dot ${customer.status || 'offline'}`}></span>
            {customer.status === 'online' ? 'Online' : 'Offline'} • {customer.phoneNumber || customer.phone || 'N/A'}
          </p>
        </div>
        <div className="chat-header-actions">
          <button className="icon-button" title="Call">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
            </svg>
          </button>
          <button className="icon-button" title="More options">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="1"></circle>
              <circle cx="19" cy="12" r="1"></circle>
              <circle cx="5" cy="12" r="1"></circle>
            </svg>
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <p>{typeof error === 'string' ? error : error.message || 'An error occurred'}</p>
          <button onClick={() => dispatch(clearError())}>✕</button>
        </div>
      )}

      {loadingConversation ? (
        <div className="chat-messages loading">
          <div className="spinner">Loading conversation...</div>
        </div>
      ) : (
        <div className="chat-messages">
          {Object.keys(groupedMessages).length === 0 && (
            <div className="empty-conversation">
              <p>No messages yet. Start the conversation!</p>
              <button
                className="start-conversation-btn"
                onClick={handleSendWelcome}
                disabled={isSendingWelcome}
              >
                {isSendingWelcome ? 'Sending...' : '👋 Send Welcome Message'}
              </button>
            </div>
          )}
          {Object.keys(groupedMessages).length > 0 && (
            Object.entries(groupedMessages).map(([date, dateMessages]) => {
              return (
                <div key={date} className="message-group">
                  <div className="date-divider">
                    <span>{date}</span>
                  </div>
                  {dateMessages.map(msg => (
                    <div key={msg.id} className={`message ${msg.sender === 'business' ? 'message-sent' : 'message-received'}`} ref={messagesEndRef}>
                      <div className="message-bubble">
                        {/* Display received media content */}
                        {msg.fullMediaUrl && msg.messageType && (
                          <div className="message-media">
                            {(msg.messageType === 'image' || msg.content?.mimeType?.startsWith('image/')) && (
                              <img loading='lazy' src={msg.fullMediaUrl} alt="Received media" className="message-image" />
                            )}
                            {(msg.messageType === 'document' || msg.content?.mimeType?.startsWith('application/')) && (
                              <a href={msg.fullMediaUrl} target="_blank" rel="noopener noreferrer" className="message-document">
                                <div className="document-icon">📄</div>
                                <div className="document-info">
                                  <div className="document-name">{msg.content?.filename || 'Document'}</div>
                                  <div className="document-size">{msg.content?.mimeType || 'File'}</div>
                                </div>
                                <div className="download-icon">⬇️</div>
                              </a>
                            )}
                            {(msg.messageType === 'video' || msg.content?.mimeType?.startsWith('video/')) && (
                              <video controls className="message-video" width="280" height="200">
                                <source src={msg.fullMediaUrl} type={msg.content?.mimeType || 'video/mp4'} />
                                Your browser does not support the video tag.
                              </video>
                            )}
                            {(msg.messageType === 'audio' || msg.content?.mimeType?.startsWith('audio/')) && (
                              <audio controls className="message-audio">
                                <source src={msg.fullMediaUrl} type={msg.content?.mimeType || 'audio/mpeg'} />
                                Your browser does not support the audio element.
                              </audio>
                            )}
                          </div>
                        )}
                        
                        {/* Display local attachments (outbound) */}
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className="message-attachments">
                            {msg.attachments.map(att => (
                              <div key={att.id} className="message-attachment">
                                {att.type === 'image' && att.preview ? (
                                  <img loading='lazy' src={att.preview} alt={att.name} className="attachment-preview-image" />
                                ) : (
                                  <div className="attachment-preview-file">
                                    <div className="file-icon-large">
                                      {att.type === 'document' && '📄'}
                                      {att.type === 'video' && '🎥'}
                                    </div>
                                    <div className="file-name-small">{att.name}</div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {msg.text && <p className="message-text">{msg.text}</p>}
                        {msg.content?.text && !msg.text && <p className="message-text">{msg.content.text}</p>}
                        <span className="message-time">{msg.timestamp}</span>
                      </div>
                      {msg.sender === 'business' && (
                        <div className="message-status">
                          <svg width="16" height="16" viewBox="0 0 16 15" fill="none">
                            <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l1.32 1.266c.143.14.361.125.484-.033l6.272-8.175a.366.366 0 0 0-.063-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L1.891 7.769a.366.366 0 0 0-.515.006l-.423.433a.364.364 0 0 0 .006.514l3.258 3.185c.143.14.361.125.484-.033l6.272-8.175a.365.365 0 0 0-.063-.51z" fill="#4FC3F7"/>
                          </svg>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>
      )}
      <div  />
      {attachedFiles.length > 0 && (
        <div className="attachments-preview">
          {attachedFiles.map(file => (
            <div key={file.id} className="attachment-item">
              {file.type === 'image' && file.preview ? (
                <div className="attachment-image">
                  <img src={file.preview} alt={file.name} />
                  <button 
                    className="remove-attachment"
                    onClick={() => handleRemoveAttachment(file.id)}
                    title="Remove"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="attachment-file">
                  <div className="file-icon">
                    {file.type === 'document' && '📄'}
                    {file.type === 'video' && '🎥'}
                  </div>
                  <div className="file-info">
                    <div className="file-name">{file.name}</div>
                    <div className="file-size">{formatFileSize(file.size)}</div>
                  </div>
                  <button 
                    className="remove-attachment"
                    onClick={() => handleRemoveAttachment(file.id)}
                    title="Remove"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="chat-input-container">
        <form onSubmit={handleSendMessage} className="chat-input-form">
          <div className="attach-button-wrapper" ref={attachMenuRef}>
            <button 
              type="button" 
              className="attach-button" 
              title="Attach file"
              onClick={handleAttachClick}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
              </svg>
            </button>
            
            {showAttachMenu && (
              <div className="attach-menu">
                <button 
                  type="button" 
                  className="attach-menu-item"
                  onClick={() => handleAttachOption('document')}
                >
                  <div className="menu-item-icon">📄</div>
                  <div className="menu-item-content">
                    <div className="menu-item-title">Document</div>
                    <div className="menu-item-subtitle">PDF, Word, Excel, etc.</div>
                  </div>
                </button>
                
                <button 
                  type="button" 
                  className="attach-menu-item"
                  onClick={() => handleAttachOption('image')}
                >
                  <div className="menu-item-icon">🖼️</div>
                  <div className="menu-item-content">
                    <div className="menu-item-title">Photo & Video</div>
                    <div className="menu-item-subtitle">Images and videos</div>
                  </div>
                </button>
                
                <button 
                  type="button" 
                  className="attach-menu-item"
                  onClick={() => handleAttachOption('video')}
                >
                  <div className="menu-item-icon">🎥</div>
                  <div className="menu-item-content">
                    <div className="menu-item-title">Video</div>
                    <div className="menu-item-subtitle">Video files</div>
                  </div>
                </button>
                
                <button 
                  type="button" 
                  className="attach-menu-item"
                  onClick={() => handleAttachOption('location')}
                >
                  <div className="menu-item-icon">📍</div>
                  <div className="menu-item-content">
                    <div className="menu-item-title">Location</div>
                    <div className="menu-item-subtitle">Share your location</div>
                  </div>
                </button>
                
                <button 
                  type="button" 
                  className="attach-menu-item"
                  onClick={() => handleAttachOption('contact')}
                >
                  <div className="menu-item-icon">👤</div>
                  <div className="menu-item-content">
                    <div className="menu-item-title">Contact</div>
                    <div className="menu-item-subtitle">Share a contact</div>
                  </div>
                </button>
                
                <button 
                  type="button" 
                  className="attach-menu-item"
                  onClick={() => handleAttachOption('catalog')}
                >
                  <div className="menu-item-icon">📦</div>
                  <div className="menu-item-content">
                    <div className="menu-item-title">Product</div>
                    <div className="menu-item-subtitle">From catalog</div>
                  </div>
                </button>
              </div>
            )}

            {/* Hidden file inputs */}
            <input
              ref={documentInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => handleFileSelect('document', e)}
            />
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => handleFileSelect('image', e)}
            />
            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => handleFileSelect('video', e)}
            />
          </div>
          
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            className="chat-input"
            rows="1"
          />
          <button
            type="button"
            className="welcome-button"
            title="Send Welcome Message"
            onClick={handleSendWelcome}
            disabled={isSendingWelcome}
          >
            {isSendingWelcome ? (
              <span className="spinner-small">⟳</span>
            ) : (
              <FiMessageSquare size={20} />
            )}
          </button>
          <button 
            type="submit" 
            className="send-button" 
            disabled={(!message.trim() && attachedFiles.length === 0) || isSending}
          >
            {isSending ? (
              <span className="spinner-small">⟳</span>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Chat;

